"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import {
	fetchSupersetCharts,
	fetchSupersetStatus,
} from "../../../api/supersetApi";
import { ChartCard } from "../components/ChartCard";
import { EmbedPanel } from "../components/EmbedPanel";

const DEFAULT_USER_ADDRESS = "0x78f329afce2bf9100882e0ee1f83b1e1050c9529";

type Mode = "native" | "embed";

function readModeFromUrl(): Mode {
	if (typeof window === "undefined") return "native";
	return new URLSearchParams(window.location.search).get("mode") === "embed"
		? "embed"
		: "native";
}

function parseUserAddressFromUrl(): string {
	if (typeof window === "undefined") return DEFAULT_USER_ADDRESS;
	const pathname = window.location.pathname;
	const match = pathname.match(/^(?:\/dashboard)?\/users\/([^/]+)/);
	if (match && match[1]) {
		return match[1];
	}
	const searchUser = new URLSearchParams(window.location.search).get("user");
	return searchUser || DEFAULT_USER_ADDRESS;
}

function syncUserUrl(userAddress: string, replace = false) {
	if (typeof window === "undefined") return;
	const mode = readModeFromUrl();
	const modeParam = mode === "embed" ? "?mode=embed" : "";
	const nextUrl = `/dashboard/users/${userAddress}${modeParam}`;

	const current = window.location.pathname + window.location.search;
	if (current === nextUrl) return;

	if (replace) {
		window.history.replaceState(null, "", nextUrl);
	} else {
		window.history.pushState(null, "", nextUrl);
	}
}

// Curated order of User Intelligence charts
const USER_PRIORITY_CHART_IDS = [
	23, // 📊 User Stats Snapshot
	24, // 📈 Cumulative PnL Timeline
	25, // 🏷️ Category Performance
	28, // 🔍 Recent Trades
	26, // ⏳ Holding Duration vs Profitability
	27, // 💰 Confidence vs Win Rate
];

export function UserDashboardPage() {
	const [mode] = useState<Mode>(readModeFromUrl);
	const initialAddress = useMemo(parseUserAddressFromUrl, []);
	const [userAddress, setUserAddress] = useState<string>(initialAddress);
	const [inputAddress, setInputAddress] = useState<string>(initialAddress);
	const [copied, setCopied] = useState(false);
	const [loadingChartIds, setLoadingChartIds] = useState<Set<number>>(
		new Set(),
	);

	// Normalize deep-link URL on first mount if accessed via /users/:address or ?user=
	useEffect(() => {
		syncUserUrl(initialAddress, true);
	}, [initialAddress]);

	// Keep URL synced when browser back/forward buttons are clicked
	useEffect(() => {
		const handlePopState = () => {
			const popAddress = parseUserAddressFromUrl();
			setUserAddress(popAddress);
			setInputAddress(popAddress);
		};
		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, []);

	const handleAddressSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			const trimmed = inputAddress.trim();
			if (trimmed && trimmed !== userAddress) {
				setUserAddress(trimmed);
				syncUserUrl(trimmed, false);
			}
		},
		[inputAddress, userAddress],
	);

	const handleCopy = useCallback(() => {
		if (navigator.clipboard) {
			void navigator.clipboard.writeText(userAddress);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	}, [userAddress]);

	const handleChartLoadingChange = useCallback(
		(chartId: number, loading: boolean) => {
			setLoadingChartIds((prev) => {
				const isLoading = prev.has(chartId);
				if (loading === isLoading) return prev;
				const next = new Set(prev);
				if (loading) next.add(chartId);
				else next.delete(chartId);
				return next;
			});
		},
		[],
	);

	const { data: status } = useSWR(
		["superset-status"],
		() => fetchSupersetStatus(),
		{ revalidateOnFocus: false },
	);

	const { data: charts, error: chartsError } = useSWR(
		mode === "native" && status?.configured
			? ["superset-user-charts"]
			: null,
		() => fetchSupersetCharts("user"),
		{ revalidateOnFocus: false },
	);

	const orderedCharts = useMemo(() => {
		if (!charts) return [];
		const priorityIndex = new Map(
			USER_PRIORITY_CHART_IDS.map((id, i) => [id, i]),
		);
		return [...charts].sort((a, b) => {
			const pa = priorityIndex.get(a.id) ?? 99;
			const pb = priorityIndex.get(b.id) ?? 99;
			return pa - pb;
		});
	}, [charts]);

	return (
		<div className="terminal-app">
			<main className="container mx-auto max-w-6xl px-4 py-6">
				{/* Top Header - Focused on Trader Profile */}
				<div className="mb-6 pb-4 border-b border-base-content/10 flex items-start justify-between gap-4 flex-wrap">
					<div>
						<h1 className="text-lg md:text-xl font-bold text-base-content font-mono flex items-center gap-2">
							<span className="text-primary">$</span> trader intelligence
						</h1>
						<div className="flex items-center gap-2 mt-1.5 flex-wrap">
							<span className="text-xs font-mono text-base-content/50">Wallet:</span>
							<span className="font-mono text-xs md:text-sm font-bold text-primary">
								{userAddress}
							</span>
							<button
								type="button"
								onClick={handleCopy}
								className="btn btn-ghost btn-xs text-[10px] text-base-content/60 hover:text-base-content"
							>
								{copied ? "copied!" : "copy"}
							</button>
						</div>
					</div>

					<div className="flex items-center gap-3 text-xs font-mono text-base-content/60">
						<a
							href={`https://polymarket.com/profile/${userAddress}`}
							target="_blank"
							rel="noopener noreferrer"
							className="hover:text-primary transition-colors flex items-center gap-1"
						>
							<span>Polymarket Profile</span>
							<span>↗</span>
						</a>
						<span>•</span>
						<a
							href={`https://polygonscan.com/address/${userAddress}`}
							target="_blank"
							rel="noopener noreferrer"
							className="hover:text-primary transition-colors flex items-center gap-1"
						>
							<span>Polygonscan</span>
							<span>↗</span>
						</a>
					</div>
				</div>

				{/* Unconfigured Alert */}
				{status && !status.configured && (
					<div className="card bg-base-300 border border-warning/30 mb-6">
						<div className="card-body p-4 text-xs font-mono text-warning">
							Superset isn't configured on this server yet (missing credentials).
						</div>
					</div>
				)}

				{/* Loading indicator */}
				{loadingChartIds.size > 0 && (
					<div className="flex items-center gap-2 text-[11px] font-mono text-primary/80 mb-4">
						<span className="inline-block w-3 h-3 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
						Refreshing {loadingChartIds.size} chart
						{loadingChartIds.size === 1 ? "" : "s"}…
					</div>
				)}

				{/* Charts Grid */}
				{mode === "native" && status?.configured && (
					<>
						{chartsError && (
							<div className="text-xs font-mono text-error py-8 text-center">
								Couldn't load user charts.
							</div>
						)}
						{!chartsError && !charts && (
							<div className="flex items-center justify-center gap-2 text-xs font-mono text-base-content/50 py-10">
								<span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
								<span>Loading trader intelligence charts…</span>
							</div>
						)}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{orderedCharts.map((chart) => (
								<ChartCard
									key={chart.id}
									chart={chart}
									userAddress={userAddress}
									onLoadingChange={handleChartLoadingChange}
								/>
							))}
						</div>
					</>
				)}

				{mode === "embed" && status?.configured && <EmbedPanel type="user" />}

				{/* BOTTOM SECTION: SEARCH USER AT THE BOTTOM */}
				<div className="card bg-base-200 border border-base-content/10 mt-12 mb-6">
					<div className="card-body p-4 md:p-5">
						<div className="text-[10px] font-mono uppercase tracking-wider text-base-content/50 mb-1">
							🔍 Search &amp; Inspect Another Wallet
						</div>
						<form
							onSubmit={handleAddressSubmit}
							className="flex flex-col md:flex-row gap-2.5 items-stretch md:items-center"
						>
							<input
								type="text"
								value={inputAddress}
								onChange={(e) => setInputAddress(e.target.value)}
								placeholder="Enter 0x... wallet address"
								className="input input-sm bg-base-300 font-mono text-xs text-base-content border border-base-content/15 rounded flex-1 max-w-lg focus:outline-primary"
							/>
							<button
								type="submit"
								className="btn btn-sm btn-primary font-mono text-xs shrink-0"
							>
								Inspect Wallet ↗
							</button>
						</form>
					</div>
				</div>

				{/* BOTTOM SECTION: NAVIGATION AT THE VERY BOTTOM */}
				<footer className="flex items-center justify-between gap-4 flex-wrap pt-4 pb-8 border-t border-base-content/10 text-xs font-mono text-base-content/60">
					<div className="flex items-center gap-3 flex-wrap">
						<a
							href="/dashboard"
							className="hover:text-primary transition-colors"
						>
							🌐 Market Overview
						</a>
						<span>•</span>
						<a
							href="/dashboard/users/0x78f329afce2bf9100882e0ee1f83b1e1050c9529"
							className="hover:text-primary transition-colors text-primary font-bold"
						>
							👤 Trader Intelligence
						</a>
						<span>•</span>
						<a
							href="https://dash.polygains.com"
							target="_blank"
							rel="noopener noreferrer"
							className="hover:text-primary transition-colors flex items-center gap-1"
						>
							<span>📈 Metabase (dash.polygains.com)</span>
							<span>↗</span>
						</a>
					</div>
					<div>
						<a
							href="/"
							className="hover:text-primary transition-colors"
						>
							← Back to Terminal
						</a>
					</div>
				</footer>
			</main>
		</div>
	);
}
export default UserDashboardPage;
